import * as React from 'react'
import { useNavigate } from 'react-router-dom'
import { Store, Plus, ChevronRight } from 'lucide-react'
import { useVendors } from '@/hooks/useVendors'
import { requestErrorMessage } from '@/helpers/apiClient'
import type { VendorListDto } from '@lib/dto/vendor.dto'
import { Button } from '@/components/ui/button'
import { LoadingState, ErrorState, EmptyState } from '@/components/ui/states'
import { PageContainer } from '@/components/ui/page-container'
import { PageHeader } from '@/components/ui/page-header'
import { Pagination } from '@/components/ui/pagination'
import { serverPage } from '@/lib/pagination'

/** Platform vendor administration (super admin only): a calm list; creating a
 * vendor and managing its details/users/venues happen on the vendor's own page. */
export function VendorsPage() {
  const navigate = useNavigate()
  const [page, setPage] = React.useState(1)
  const vendorsQuery = useVendors(page)

  function renderList() {
    if (vendorsQuery.isPending) return <LoadingState label="Loading vendors…" />
    if (vendorsQuery.isError) {
      return <ErrorState message={requestErrorMessage(vendorsQuery.error)} onRetry={() => void vendorsQuery.refetch()} />
    }
    const list: VendorListDto = vendorsQuery.data
    // `total`, not the row count: an out-of-range page comes back with no rows,
    // and keying the empty state off that would tell somebody their catalog was
    // empty when it is only this page that is.
    if (list.total === 0) {
      return (
        <EmptyState
          icon={Store}
          title="No vendors yet"
          description="Add your first supplier to the platform."
          action={
            <Button onClick={() => navigate('/admin/vendors/new')}>
              <Plus className="h-4 w-4" /> New vendor
            </Button>
          }
        />
      )
    }
    // The rows are the page the API returned; `total` is the whole list.
    const shown = serverPage(page, list.total, list.vendors.length)
    return (
      <div className="overflow-hidden rounded-md border border-line">
        <ul className="divide-y divide-line">
        {list.vendors.map((vendor) => (
          <li key={vendor.id}>
            <button
              type="button"
              onClick={() => navigate(`/admin/vendors/${vendor.id}`)}
              className="flex w-full items-center gap-3 px-4 py-3 text-left transition-colors hover:bg-surface-hover"
            >
              {vendor.coverImageUrl ? (
                <img
                  src={vendor.coverImageUrl}
                  alt=""
                  className="h-8 w-11 shrink-0 border border-line object-cover"
                />
              ) : (
                <span className="grid h-8 w-11 shrink-0 place-items-center border border-line bg-surface-sunken">
                  <Store className="h-4 w-4 text-ink-muted" />
                </span>
              )}
              <span className="min-w-0 flex-1 truncate font-medium">{vendor.name}</span>
              <span className="text-sm text-ink-muted">
                {vendor.deactivatedAt !== null ? 'Deactivated · ' : ''}
                {vendor.userCount} {vendor.userCount === 1 ? 'user' : 'users'}
              </span>
              <ChevronRight className="h-4 w-4 shrink-0 text-ink-muted" />
            </button>
          </li>
        ))}
        </ul>
        {/* Inside the box, like a table's footer: the bar belongs to the list. */}
        <Pagination
          page={shown.page}
          pageCount={shown.pageCount}
          from={shown.from}
          to={shown.to}
          total={shown.total}
          unit="vendors"
          onPage={setPage}
        />
      </div>
    )
  }

  return (
    <PageContainer width="wide" className="space-y-6">
      <div className="flex items-start justify-between gap-4">
        <PageHeader
          title="Vendors"
          description="Suppliers managed by their own users."
          meta={vendorsQuery.data ? `${vendorsQuery.data.total} total` : undefined}
        />
        <Button onClick={() => navigate('/admin/vendors/new')}>
          <Plus className="h-4 w-4" /> New vendor
        </Button>
      </div>

      {renderList()}
    </PageContainer>
  )
}
